import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Price from "../../components/price/Price";
import {
    useSaveTransactionConsultationMutation,
    useAddOrderMutation
} from "../../services/orderService";
import { toast } from "react-toastify";
import parseError from "../../utils/errorUtils";
import useQuery from "../../hooks/useQuery";
import { useGetPricingInfoQuery, useGetBillingInfoQuery } from "../../services/paymentService";
import { AppointmentContext } from "../../providers/AppointmentProvider";
import { useLoggerUtils } from "../../utils/loggerUtils";
import Button from "../../components/button/Button";

function PaymentSuccessPage() {
    const navigate = useNavigate();
    const query = useQuery();
    const [saveTransactionConsultation] = useSaveTransactionConsultationMutation();
    const subscribed = localStorage.getItem("subscribed");
    const sessionId = localStorage.getItem("sessionId");
    const selectedPackage = localStorage.getItem("paymentMode");
    const isConsultation = query.get("isConsultation");
    const isCallToBook = query.get("isCallToBook");
    const [isInnerLoading, setIsInnerLoading] = useState(false);
    const [addOrder] = useAddOrderMutation();
    const { data: billingInfo } = useGetBillingInfoQuery();
    const { info } = useLoggerUtils();
    const [orderId, setOrderId] = useState();
    const { appointmentData } = useContext(AppointmentContext);
    const { data: subscriptionPricing, isSubscriptionLoading } = useGetPricingInfoQuery({
        type: "Subscription"
    });

    useEffect(() => {
        if (isConsultation === "true" && sessionId) handleAddOrderConsultation(isConsultation, sessionId, subscribed);
    }, [query, isConsultation, sessionId]);

    useEffect(() => {
        if (Object.keys(appointmentData || {}).length === 0) return;
        if (isCallToBook === "true") {
            handleAddOrder();
        }
    }, [isCallToBook, appointmentData?.id])

    const saveConsultationTransactionAPI = async (sessionId, orderId, paymentType) => {
        try {
            setIsInnerLoading(true);
            const res = await saveTransactionConsultation({
                sessionId: sessionId ? sessionId : "",
                orderItemId: orderId,
                orderType: 'REPEAT',
                mode: paymentType,
            });

            if (res.error) {
                toast.error(parseError(res.error, "Couldn't place order"));
            } else {
                localStorage.removeItem("paymentMode");
                localStorage.removeItem("sessionId");
                localStorage.removeItem("orderData");
            }
        } catch (error) {
            console.log("Stripe Save Transaction : ", error);
        } finally {
            setIsInnerLoading(false);
        }
    };

    const handleAddOrderConsultation = async (isConsultation, sessionId, subscribed) => {
        const orderData = JSON.parse(localStorage.getItem("orderData"));
        if (orderData) {
            try {
                let payload = orderData;
                if (orderData?.patient?.preferences?.TEMPORARY_APPOINTMENT) {
                    payload = { ...orderData, source: "CALL_BOOKING" };
                }
                const res = await addOrder({ type: "BOOKING", values: payload });

                if (res.error) {
                    // handle error
                    toast.error(parseError(res.error, "Couldn't place order"));
                } else {
                    // handle success
                    if (isConsultation === "true" && res?.data?.message && sessionId && subscribed !== "paid") {
                        saveConsultationTransactionAPI(sessionId, res.data.message, "PAYMENT");
                    }

                    if (isConsultation === "true" && subscribed === "paid") {
                        saveConsultationTransactionAPI(sessionId, res?.data?.message, "SUBSCRIPTION");
                    }
                }
            } catch (e) {
                console.error(e);
            }
        }
    };

    const handleAddOrder = async () => {
        if (sessionId) {
            const orderData = {
                orderNotes: [],
                deliveryType: "STANDARD",
                paymentType: "ONLINE",
                deviceType: "OTHER",
                info: {
                    PRESENTING_COMPLAINT: appointmentData?.notes || "",
                    COUPON: "",
                },
                name: appointmentData?.name,
                type: appointmentData?.type,
                appointment: {
                    id: Number(appointmentData?.id),
                },
                patient: appointmentData?.patient,
                isRepeatOrder: false,
            };

            try {
                let payload = orderData;
                if (orderData?.patient?.preferences?.TEMPORARY_APPOINTMENT) {
                    payload = { ...orderData, source: 'CALL_BOOKING' };
                }
                const res = await addOrder({ type: "BOOKING", values: payload });

                if (res.error) {
                    toast.error(parseError(res.error, "Couldn't place order"));
                } else {
                    setOrderId(res.data.message)
                    await paymentConfirm(res.data.message);
                }
            } catch (e) {
                console.error(e);
            }
        }
    };

    const paymentConfirm = async (orderItemId) => {
        if (sessionId) {
            try {
                const res = await saveTransactionConsultation(
                    {
                        sessionId: sessionId,
                        orderItemId: orderItemId,
                        orderType: 'REPEAT',
                        mode: selectedPackage
                    }
                );

                if (res.error) {
                    toast.error(parseError(res.error, "Save transaction is failed"));
                }
            } catch (e) {
                info("addOrder - catch: " + JSON.stringify(e));
                console.error(e);
            } finally {
                localStorage.removeItem("sessionId");
            }
        }
    }

    const handleNavigation = () => {
        if (isConsultation === "true") {
            navigate('/user/dashboard')
        } else if (isCallToBook === "true") {
            localStorage.removeItem("paymentMode");
            navigate(
                `/onboarding/appointment/${orderId}/documents`,
                {
                    replace: true,
                }
            );
        }
    }

    return (
        <div
            className="row mx-0 align-items-center justify-content-center bg-light-gray"
            style={{
                minHeight: `calc(100vh - 80px)`,
            }}
        >
            <div className="col-12 col-md-10 col-xl-4 col-xxl-3 mx-auto pb-12">
                <img
                    src="/images/vectors/success.svg"
                    className="img-fluid mx-auto d-block"
                    alt=""
                />
                <div className="fw-bold fs-4 text-brown-black text-center mt-3">
                    Your order has been placed successfully.
                </div>

                <div className="mt-3 text-dark-4 fs-6 text-center px-xl-2">
                    We'll get your prescription sorted. You can check the status
                    of your order through the Orders Summary
                </div>

                {isConsultation === "true" && (
                    <div className="bg-white p-4 border mt-6">
                        <h3 className="text-center mb-5 fw-bold text-brown-black">
                            Payment Summary
                        </h3>

                        <div className="d-flex mb-3 align-items-center justify-content-between text-medium-gray fs-6">
                            <div>Consultation fee</div>
                            <div>
                                <Price
                                    amount={billingInfo?.repeatAppointmentPrice}
                                    currency="GBP"
                                />
                            </div>
                        </div>
                        <hr />
                        <div className="d-flex mb-3 align-items-center fw-bold justify-content-between text-primary fs-5">
                            <div>Total</div>
                            <div>
                                <Price
                                    amount={
                                        Number(billingInfo?.repeatAppointmentPrice)
                                    }
                                    currency="GBP"
                                />
                            </div>
                        </div>
                    </div>
                )}
                {selectedPackage && (
                    <div>
                        <h6 className="fw-bold text-center text-primary mb-2 mt-5 subscription-details">
                            Subscription details
                        </h6>
                        <div className="subscription-card w-100">
                            <div className="col subscription-card-col">
                                <div className="row align-items-center">
                                    {selectedPackage === "SUBSCRIPTION" && (
                                        <label className="subscription-card-heading">
                                            Treat it clinic subscription
                                        </label>
                                    )}
                                    {selectedPackage === "PAYMENT" && (
                                        <label className="subscription-card-heading">
                                            Treat it . Pay as You go
                                        </label>
                                    )}

                                    {selectedPackage === "SUBSCRIPTION" && (
                                        <div className="d-flex direction-row subscription-status-btn align-items-center">
                                            <div className="subscription-status-icon"></div>
                                            <label className="subscription-status">Active</label>
                                        </div>
                                    )}
                                </div>

                                {selectedPackage === "SUBSCRIPTION" && (
                                    <div className="row align-items-center">
                                        <label className="subscription-price">£{subscriptionPricing?.amount || "14.5"}</label>
                                        <label className="subscription-monthly">Monthly</label>
                                    </div>
                                )}

                                {selectedPackage === "PAYMENT" && (
                                    <div className="row align-items-center">
                                        <label className="subscription-price">£9.99</label>
                                        <label className="subscription-monthly">
                                            Repeat prescription
                                        </label>
                                    </div>
                                )}
                                <label className="subscription-text">
                                    Your last payment was successful. You can go ahead and
                                    place the order.
                                </label>
                            </div>
                        </div>
                    </div>
                )}
                <div className="mt-5">
                    <Button
                        class="link-btn bg-primary d-flex text-white justify-content-center border-0 rounded p-2 p-sm-2 me-5"
                        fullWidth={true}
                        onClick={handleNavigation}
                    >
                        Continue
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default PaymentSuccessPage